import { forwardRef, useMemo, useState } from 'react';
import {
  AppTable,
  BorderTableRow,
  type AppTableRowProps,
} from '@components/AppTable';
import { LoaderBar } from '@components/LoaderBar';
import { cn } from '@skupreme/shared/ui';
import { OrderStatus } from '@utility/constants/common';
import OrdersService from '@utility/services/order';
import { BatchRow } from './row';
import type { ITableColumn } from 'types/common/table';

type BatchOrderTableRowProps = AppTableRowProps & {
  greyed?: number;
  checked?: number;
};

export const BatchOrderTableRow = forwardRef<
  HTMLTableRowElement,
  BatchOrderTableRowProps
>(function BatchOrderTableRowPart(
  { greyed, checked, className, ...rest },
  ref,
) {
  return (
    <BorderTableRow
      ref={ref}
      {...rest}
      className={cn(
        'sk:text-xs',
        checked || greyed ? 'sk:bg-fill-secondary' : 'sk:bg-fill-quaternary',
        className,
      )}
    />
  );
});

export function BatchOrderList({
  selectedOrder,
  loading,
  data,
  toggleSelection,
  openPrintPopup,
  pickAndShipClick,
  saving,
  deleteBatch,
  deleteOrder,
  status,
  fulfillBatch,
  editBatch,
}) {
  const [orderRates, setOrderRates] = useState({});
  const [rateLoaders, setRateLoaders] = useState({});

  const setLoader = (orderId, value) => {
    setRateLoaders(rateLoaders => ({
      ...rateLoaders,
      [orderId]: value,
    }));
  };

  const columns: ITableColumn[] = useMemo(() => {
    const arr: ITableColumn[] = [
      {
        text: 'Name',
        key: 'name',
        textAlign: 'left',
        width: 2,
      },
      {
        text: 'Date Created',
        key: 'createdAt',
        textAlign: 'left',
        width: 3,
      },
      {
        text: 'Fulfillment Provider',
        key: 'fp',
        width: 3,
        textAlign: 'left',
      },
      {
        text: 'Total Items',
        key: 'total_items',
        textAlign: 'left',
        width: 1,
      },
      {
        text: status === OrderStatus.Shipped ? '' : 'Status',
        key: 'status',
        width: 3,
        textAlign: 'left',
      },
    ];

    return arr;
  }, [status]);

  const fetchRates = async batch => {
    const chunkSize = 2;

    for (let i = 0; i < batch.orders.length; i += chunkSize) {
      const chunk = batch.orders.slice(i, i + chunkSize);

      const promises = chunk.map(async order => {
        const key = `${order._id}-${batch?.fulfillment_provider_id?._id}`;

        // skip if already shipped or fetched
        if (order.status === OrderStatus.Completed || orderRates[key]) {
          setLoader(order._id, false);
          return;
        }

        try {
          // show loader
          setLoader(order._id, true);

          const payload = {
            fulfillment_provider_id: batch?.fulfillment_provider_id?._id,
            order_id: order._id,
            order_speed: 'Standard',
            items: order.items.map(item => ({
              qty: item.qty,
              item_id: item.item_id,
            })),
          };

          const rate = await OrdersService.providerShippingRate(payload);

          // update state immediately for this order
          setOrderRates(rates => ({
            ...rates,
            [key]: rate,
          }));
        } catch (err) {
          console.error(`Rate fetch failed for order ${order._id}`, err);
        } finally {
          // hide loader regardless of success/failure
          setLoader(order._id, false);
        }
      });

      // run up to 2 in parallel

      await Promise.all(promises);
    }
  };

  return (
    <>
      {saving && <LoaderBar content="Saving" />}
      <AppTable
        data={data}
        fixed
        RowComponent={
          // @ts-expect-error ts-migrate(2322) FIXME: Type '{ selectedOrder: any; toggleSelection: ... Remove this comment to see the full error message
          <BatchRow
            selectedOrder={selectedOrder}
            toggleSelection={toggleSelection}
            openPrintPopup={openPrintPopup}
            pickAndShipClick={pickAndShipClick}
            deleteBatch={deleteBatch}
            deleteOrder={deleteOrder}
            status={status}
            fulfillBatch={fulfillBatch}
            editBatch={editBatch}
            rateLoaders={rateLoaders}
            orderRates={orderRates}
            fetchRates={fetchRates}
            isShipped={status === OrderStatus.Shipped}
          />
        }
        columns={columns}
        loading={loading}
        loadingRows={5}
      />
    </>
  );
}
